import { Box, Colors, IcEmpty, Text } from '@fozito/common-ui';
import moment from 'moment';
import React, { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { formatPhone } from 'utils/formatPhone';

const OrderDetailsContainer = styled.div`
border-right: 1px solid #86909C;
box-shadow: 8px 0px 16px 0px rgba(0, 0, 0, 0.25);
padding: 8px 16px;
padding-left: 0;
display: flex;
flex-direction: column;
`;
const Divider = styled.div`
  height: 0.5px;
  background: ${Colors.neutral.border[2]};
  margin: 0;
`;

const OrderItemList = styled.div`
  margin-bottom: 8px;
  text-align: left;
  flex: 1;
  position: relative;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  align-self: stretch;
`;

const OrderItemLeft = styled.div`
  display: flex;
  align-items: center;
`;

const OrderItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OrderTotalsSection = styled.div``;

const OrderTotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TicketTimeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid #CCD4DC;
  margin-bottom: 8px;
  padding-bottom: 8px;
  .customer-name {
    color: #1D2129;
    text-align: right;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
`;

const TicketNumber = styled.div`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  background: #232F3E;
`;

const DiscountText = styled(Text)`
color: #D2464F;
text-align: right;
font-family: Poppins;
font-size: 16px;
font-style: normal;
line-height: normal;
text-decoration-line: strikethrough;
text-transform: capitalize;
text-decoration: line-through;
`;

export type IOrderDetailItemValue = {
  name: string;
  price: number;
  quantity: number;
  discount: number;
  requiredMember?: boolean;
};
interface OrderDetailsProps {
  date: string;
  customerName: string;
  customerPhone: string;
  items: Array<IOrderDetailItemValue>;
  subtotal: number;
  cardFee?: number;
  saleTax?: number;
  useTax?: number;
  discount?: number;
  ticketNumber?: number;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  items = [],
  subtotal,
  cardFee = 0,
  saleTax = 0,
  useTax = 0,
  discount = 0,
  customerName,
  customerPhone,
  ticketNumber,
  date,
}) => {
  const _date = useMemo(() => moment(date, 'MM-DD-YYYY hh:mm A'), [date]);
  const renderPrice = (item: IOrderDetailItemValue) => {
    if (!item.discount) return (
      <Text title_h8_18_medium style={{ fontSize: 20 }} color_text_5>
        {formatCurrency(item.price)}
      </Text>
    );

    const amount = item.price * item.quantity;

    const result = amount - item.discount;

    return <>
      <DiscountText color={Colors.primary[6]}>
        {formatCurrency(amount)}
      </DiscountText>
      <Text title_h8_18_medium style={{ fontSize: 20 }} color_text_5>
        {formatCurrency(result > 0 ? result : 0)}
      </Text>
    </>;
  };

  return (
    <OrderDetailsContainer>
      <TicketTimeContainer>
        <Box display='flex' alignItems='center' gap={'16px'}>
          <TicketNumber>
            <Text title_h7_20_semibold color_text_1>#{ticketNumber}</Text>
          </TicketNumber>
          <Box display='flex' flexDirection='column'>
            <Text>{_date?.format('MM-DD-YYYY') || '--'}</Text>
            <Text>{_date?.format('hh:mm A') || '--'}</Text>
          </Box>
        </Box>
        <Box display='flex' justifyContent='flex-end' alignItems='center' gap={'10px'} flex={1}>
          <div>
            <div className="customer-name">{customerName}</div>
            <div className="customer-name">{customerPhone ? formatPhone(customerPhone) : '--'}</div>
          </div>
        </Box>
      </TicketTimeContainer>
      <OrderItemList>
        <Box position='absolute' inset={0} overflow='auto'>
          {!items.length && <Box paddingTop={24} display='flex' flexDirection='column' gap={16} justifyContent='center' alignItems='center'>
            <IcEmpty />
            <Text content_content_1_16_regular color_text_3> ---</Text>
          </Box>}
          {items.map((item, index) => (
            <Fragment key={item.name}>
              <Box display='flex' flexDirection='column' alignItems='end'>
                <OrderItem>
                  <OrderItemLeft>
                    <Text title_h8_18_medium style={{ fontSize: 20 }}>{item.name}</Text>
                  </OrderItemLeft>
                  <OrderItemRight>
                    <Text title_h8_18_medium style={{ width: 34 }}>
                      x{item.quantity}
                    </Text>
                    <Box width={200} display='flex' justifyContent='flex-end' gap={16}>
                      {renderPrice(item)}
                    </Box>
                  </OrderItemRight>
                </OrderItem>
                {!!item?.requiredMember && <Box padding={'2px 8px'} backgroundColor='#FF7D00' borderRadius={4} marginBottom={4}>
                  <Text style={{ color: '#FDF6F4', fontSize: 14, fontWeight: '500' }}>Store Member Only</Text>
                </Box>}
              </Box>
              {index !== items.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Box>
      </OrderItemList>

      <OrderTotalsSection>
        <OrderTotalsRow>
          <Text title_h7_20_semibold color_text_5>
            Subtotal:
          </Text>
          <Text title_h7_20_semibold color_text_5>
            {formatCurrency(subtotal)}
          </Text>
        </OrderTotalsRow>
        <OrderTotalsRow>
          <Text title_h8_18_medium color_text_5>
            Card Fee:
          </Text>
          <Text title_h8_18_medium color_text_5>
            {formatCurrency(cardFee)}
          </Text>
        </OrderTotalsRow>
        <OrderTotalsRow>
          <Text title_h8_18_medium color_text_5>
            Sale Tax:
          </Text>
          <Text title_h8_18_medium color_text_5>
            {formatCurrency(saleTax)}
          </Text>
        </OrderTotalsRow>
        <OrderTotalsRow>
          <Text title_h8_18_medium color_text_5>
            Used Tax:
          </Text>
          <Text title_h8_18_medium color_text_5>
            {formatCurrency(useTax)}
          </Text>
        </OrderTotalsRow>
        <OrderTotalsRow>
          <Text
            title_h8_18_medium
            color_text_1
            style={{
              backgroundColor: Colors.primary[6],
              padding: '0 5px',
              borderRadius: 2,
            }}
          >
            Discounts:
          </Text>
          <Text title_h8_18_medium color={Colors.primary[6]}>
            {formatCurrency(discount)}
          </Text>
        </OrderTotalsRow>
      </OrderTotalsSection>
    </OrderDetailsContainer>
  );
};

export default OrderDetails;
